import { getAuthProfile, clearCookie } from "@/services/AuthService";
import Api from "@/services/Api";
import Cookies from "js-cookie";
import {
  PROFILE_RETRY_DELAYS_MS,
  REFRESH_MIN_MS,
  applyProfile,
  nextRefreshDelay,
} from "@/services/authRetry";

// Newest in-flight load. Only ensureAuthProfile joins it; loadAuthProfile
// always starts its own, so a caller asking after a change never gets an
// answer fetched before it.
let inFlight = null;
// True once this page load has loaded a profile successfully. Not read off
// state: vuex-persist rehydrates the module from a previous visit.
let loadedThisSession = false;
// Loads can overlap and finish out of order, so only the newest one is allowed
// to write. Bumping this also cancels the writes of everything still running.
let latestLoadId = 0;
// Background refresh pacing (refreshAuthProfile): earliest next attempt and
// the delay to use after it, doubling while the backend stays unhealthy.
let nextRefreshAt = 0;
let refreshDelayMs = REFRESH_MIN_MS;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Fetches the profile and commits it unless a newer load has started since.
// Returns whether this load itself found a usable profile.
//
// TAIR3-995: a transient backend failure (network error, timeout, 5xx) is
// retried with a short backoff before giving up, and while that happens the
// user is "pending" rather than logged out, so the navbar can say so. Only a
// definite answer from PAM (no credential) logs the user out.
const runLoad = async (commit, state) => {
  const loadId = ++latestLoadId;
  commit("SET_IN_LOADING");
  let userProfile = null;
  let transient = false;
  try {
    for (let attempt = 0; ; attempt++) {
      try {
        userProfile = await getAuthProfile();
        transient = false;
        break;
      } catch (error) {
        if (!error || !error.transient) throw error;
        transient = true;
        if (loadId !== latestLoadId) break; // superseded; stop retrying
        if (attempt >= PROFILE_RETRY_DELAYS_MS.length) break;
        commit("UPDATE_STATE", { loginPending: true });
        await sleep(PROFILE_RETRY_DELAYS_MS[attempt]);
      }
    }
  } finally {
    commit("RESET_IN_LOADING");
  }
  // make sure the user profile is not empty
  const loaded = !!(userProfile && userProfile.username);
  if (loadId !== latestLoadId) return loaded; // superseded; do not write
  if (loaded) {
    // A partial profile (a decoration step failed server-side) omits the
    // fields that step would have filled. applyProfile keeps the previous
    // values only when they belong to this same account, e.g. a persisted
    // orcidId that detail-page access depends on, and resets them otherwise.
    // The load still counts as settled for this page visit so navigation
    // never waits on a fresh load; the cookie poller refreshes a partial
    // profile in the background via refreshAuthProfile.
    commit("UPDATE_STATE", applyProfile(state, userProfile));
    commit("UPDATE_STATE", {
      loggedIn: true,
      loginPending: false,
      profileLoadFailed: !!userProfile.partial,
    });
    loadedThisSession = true;
    return true; // return true when loading the auth profile succeed
  }
  if (transient) {
    // Backend hiccup: keep whatever logged-in state we had, remember the
    // failure so the cookie poller in App.vue can try again.
    commit("UPDATE_STATE", { loginPending: false, profileLoadFailed: true });
    return false;
  }
  commit("UPDATE_STATE", {
    loggedIn: false,
    loginPending: false,
    profileLoadFailed: false,
  });
  return false; // return false when loading the auth profile failed
};

export const authProfile = {
  namespaced: true,
  state: {
    credentialId: null,
    secretKey: null,
    username: null,
    firstname: null,
    lastname: null,
    email: null,
    institution: null,
    userIdentifier: null,
    inLoading: false,
    isTairCurator: null,
    affiliations: null,
    orcidId: null,
    loggedIn: false,
    // TAIR3-995: true while a profile load is retrying after a backend hiccup
    loginPending: false,
    // TAIR3-995: true when the last load gave up on a transient failure
    profileLoadFailed: false,
  },
  mutations: {
    // A generic mutation to update state properties
    UPDATE_STATE(state, data) {
      Object.keys(data).forEach((key) => {
        if (state.hasOwnProperty(key)) {
          state[key] = data[key];
        }
      });
    },

    CLEAR_ORCID_ID(state) {
      state.orcidId = null;
    },

    RESET_STATE(state) {
      // Reset each property to its initial state
      state.credentialId = null;
      state.secretKey = null;
      state.username = null;
      state.firstname = null;
      state.lastname = null;
      state.email = null;
      state.institution = null;
      state.userIdentifier = null;
      state.inLoading = false;
      state.isTairCurator = null;
      state.affiliations = null;
      state.orcidId = null;
      state.loggedIn = false;
      state.loginPending = false;
      state.profileLoadFailed = false;
    },

    SET_IN_LOADING(state) {
      state.inLoading = true;
    },

    RESET_IN_LOADING(state) {
      state.inLoading = false;
    },
  },
  actions: {
    // Use after something changes the profile (ORCID just linked, just logged
    // in): never joins an older request, so it cannot report pre-change state.
    async loadAuthProfile({ commit, state }) {
      const load = runLoad(commit, state);
      inFlight = load;
      try {
        return await load;
      } finally {
        // Only clear if no newer load has taken over in the meantime.
        if (inFlight === load) inFlight = null;
      }
    },

    // Use when the answer only has to be current for this page load: joins an
    // in-flight load, loads when none has succeeded yet, else no-ops. A failed
    // load is not cached, so it is retried on the next call.
    async ensureAuthProfile({ dispatch }) {
      if (inFlight) return inFlight;
      if (loadedThisSession) return true;
      return dispatch("loadAuthProfile");
    },

    // Background refresh after a load failed or came back partial (the
    // cookie poller calls this). Deliberately not registered as inFlight, so
    // ensureAuthProfile (route guards) never waits on it, and skipped while
    // a foreground load is running or until the backoff has elapsed.
    async refreshAuthProfile({ commit, state }) {
      if (!state.profileLoadFailed || inFlight || state.inLoading) return;
      if (Date.now() < nextRefreshAt) return;
      nextRefreshAt = Infinity; // no overlap while this one runs
      try {
        await runLoad(commit, state);
      } finally {
        if (state.profileLoadFailed) {
          refreshDelayMs = nextRefreshDelay(refreshDelayMs);
        } else {
          refreshDelayMs = REFRESH_MIN_MS;
        }
        nextRefreshAt = Date.now() + refreshDelayMs;
      }
    },

    clearOrcidId({ commit }) {
      commit("CLEAR_ORCID_ID");
    },

    async invalidate({ commit }) {
      // Logging out must not leave the old profile cached as still current,
      // and a load already running must not write it back afterwards.
      loadedThisSession = false;
      latestLoadId += 1;
      inFlight = null;
      nextRefreshAt = 0;
      refreshDelayMs = REFRESH_MIN_MS;
      commit("RESET_STATE");
      await clearCookie(); // clear the cookie
      commit("UPDATE_STATE", { loggedIn: false }); // Set loggedIn to false after cookies are cleared
    },

    async getOrcidLoginUrl() {
      try {
        const response = await Api().get("auth/orcidloginlink");
        return response.data.url;
      } catch (e) {
        console.error("Error getting Orcid login URL", e);
      }
    },
  },
  getters: {
    getCredentials: (state) => {
      return {
        credentialId: state.credentialId,
        secretKey: state.secretKey,
      };
    },
    isAuthProfileLoading: (state) => {
      return state.inLoading;
    },
    getIsTairCurator: (state) => {
      return state.isTairCurator;
    },
    isLoggedIn: (state) => {
      // Check if the credentialId and secretKey cookies exist
      const credentialId = Cookies.get("credentialId");
      const secretKey = Cookies.get("secretKey");
      const loggedIn = !!(credentialId && secretKey);
      // Update the Vuex state based on the cookies
      state.loggedIn = loggedIn;
      return loggedIn;
    },
    getLoggedIn: (state) => {
      return state.loggedIn;
    },
    // TAIR3-995: cookies exist but the profile has not loaded yet (initial
    // load, or retrying after a backend hiccup). Lets the navbar show
    // "Signing in..." instead of a misleading Login link.
    isLoginPending: (state) => {
      const haveCookies = !!(
        Cookies.get("credentialId") && Cookies.get("secretKey")
      );
      return (
        haveCookies &&
        !state.loggedIn &&
        (state.inLoading || state.loginPending)
      );
    },
    didProfileLoadFail: (state) => state.profileLoadFailed,
    getCommunityId: (state) => {
      return state.userIdentifier;
    },
    getPartyId: (state) => {
      return state.credentialId;
    },
    getFirstName: (state) => {
      return state.firstname;
    },
    getLastName: (state) => {
      return state.lastname;
    },
    getAffiliations: (state) => {
      return state.affiliations;
    },
    getOrcidId: (state) => {
      return state.orcidId;
    },
    getLoginUrl() {
      return (
        process.env.VUE_APP_LOGIN_BASE_URL +
        "/#/contentaccess/login?partnerId=tair&redirect=" +
        process.env.VUE_APP_UI_BASE_URL +
        "/auth/login"
      );
    },
    getForgotUsernameUrl() {
      return (
        process.env.VUE_APP_LOGIN_BASE_URL +
        "/#/contentaccess/login/forgetusername?partnerId=tair"
      );
    },
  },
};
